// Service for fetching and processing email attachments
import { extractDatesFromEmail } from './emailDateExtractor';
import config from '../config';

// Fetch attachments for a specific email
export const fetchEmailAttachments = async (accessToken, messageId) => {
  try {
    if (!accessToken) {
      throw new Error('No access token provided');
    }
    
    // Get the message details first to find attachments
    const apiUrl = `https://www.googleapis.com/gmail/v1/users/me/messages/${messageId}`;
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch email details: ${response.statusText}`);
    }
    
    const data = await response.json();
    const attachments = [];
    
    // Check if the message has parts (which might contain attachments)
    if (data.payload && data.payload.parts) {
      // Find parts that are attachments
      for (const part of data.payload.parts) {
        if (part.body && part.body.attachmentId) {
          // Get attachment info
          const attachment = {
            id: part.body.attachmentId,
            filename: part.filename,
            mimeType: part.mimeType,
            size: part.body.size
          };
          
          // Only include supported attachment types
          if (isSupportedAttachment(attachment)) {
            attachments.push(attachment);
          }
        }
      }
    }
    
    console.log(`Found ${attachments.length} supported attachments in email ${messageId}`);
    return attachments;
  } catch (error) {
    console.error('Error fetching email attachments:', error);
    return [];
  }
};

// Check if attachment type is supported
const isSupportedAttachment = (attachment) => {
  // Support common image and document formats
  const supportedTypes = [
    'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp',
    'application/pdf', 'text/plain', 'text/html', 'text/csv',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx
    'application/msword', // doc
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
    'application/vnd.ms-excel' // xls
  ];
  
  return supportedTypes.includes(attachment.mimeType);
};

// Fetch actual attachment data
export const getAttachmentData = async (accessToken, messageId, attachmentId) => {
  try {
    const apiUrl = `https://www.googleapis.com/gmail/v1/users/me/messages/${messageId}/attachments/${attachmentId}`;
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch attachment data: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    // Attachment data is base64url encoded
    return {
      data: data.data,
      size: data.size
    };
  } catch (error) {
    console.error('Error fetching attachment data:', error);
    return null;
  }
};

// Convert attachment data to the appropriate format
export const processAttachment = async (attachment, attachmentData) => {
  try {
    if (!attachmentData || !attachmentData.data) {
      throw new Error('No attachment data provided');
    }
    
    // Convert base64url to base64
    const base64Data = attachmentData.data.replace(/-/g, '+').replace(/_/g, '/');
    
    // For images, return a data URL
    if (attachment.mimeType.startsWith('image/')) {
      return {
        type: 'image',
        dataUrl: `data:${attachment.mimeType};base64,${base64Data}`,
        filename: attachment.filename
      };
    }
    
    // For PDFs, return a data URL that can be rendered in an iframe or processed by PDF.js
    if (attachment.mimeType === 'application/pdf') {
      return {
        type: 'pdf',
        dataUrl: `data:application/pdf;base64,${base64Data}`,
        filename: attachment.filename
      };
    }
    
    // For text-based files, decode the content
    if (attachment.mimeType.startsWith('text/')) {
      const textContent = atob(base64Data);
      return {
        type: 'text',
        content: textContent,
        filename: attachment.filename
      };
    }
    
    // For office documents, we can't directly process them but can save as data URL
    return {
      type: 'document',
      dataUrl: `data:${attachment.mimeType};base64,${base64Data}`,
      filename: attachment.filename
    };
  } catch (error) {
    console.error('Error processing attachment:', error);
    return null;
  }
};

// Process attachments with GPT Vision API
export const processAttachmentWithGPTVision = async (attachmentData, API_KEY) => {
  try {
    if (!attachmentData || !attachmentData.dataUrl) {
      throw new Error('Invalid attachment data');
    }
    
    // Process image or PDF with GPT Vision
    // Image URLs must be properly formatted for the API
    const dataUrl = attachmentData.dataUrl;
    
    // Format the messages for the API call
    const messages = [
      {
        role: "system",
        content: "You are a precise text extractor. Extract all text from the images of documents. Preserve paragraphs, line breaks, and formatting as much as possible. Include all dates, times, and other important details."
      },
      {
        role: "user",
        content: [
          {
            type: "text",
            text: "Extract all text from this document image. Keep all the original formatting and structure."
          },
          {
            type: "image_url",
            image_url: {
              url: dataUrl,
              detail: "high"
            }
          }
        ]
      }
    ];
    
    // Call the OpenAI API (GPT-4 Vision)
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${API_KEY}`
      },
      body: JSON.stringify({
        model: "gpt-4o", // Using 4o which has vision capabilities
        messages: messages,
        max_tokens: 4096
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`API Error: ${errorData.error?.message || response.statusText}`);
    }
    
    const data = await response.json();
    
    // Extract the content from the response
    if (data.choices && data.choices[0] && data.choices[0].message) {
      return data.choices[0].message.content;
    }
    
    throw new Error('Invalid response format from Vision API');
  } catch (error) {
    console.error('Error processing attachment with GPT Vision:', error);
    return null;
  }
};

// Process text extracted from attachment with GPT-4o to extract events
export const extractEventsFromAttachmentText = async (extractedText, emailSubject, API_KEY, emailFrom = '') => {
  try {
    if (!extractedText) {
      return [];
    }
    
    // Use the same date extraction logic we use for email bodies
    const events = await extractDatesFromEmail(extractedText, emailSubject, API_KEY, emailFrom);
    
    // Add a source identifier for attachment-derived events
    return events.map(event => ({
      ...event,
      source: 'email_attachment'
    }));
  } catch (error) {
    console.error('Error extracting events from attachment text:', error);
    return [];
  }
};

// Main function to process all attachments of an email
export const processEmailAttachments = async (accessToken, messageId, emailSubject, emailFrom = '') => {
  try {
    // Get the API key
    const API_KEY = config.OPENAI_API_KEY;
    if (!API_KEY) {
      throw new Error('OpenAI API key is missing');
    }
    
    // Fetch all attachments for the email
    const attachments = await fetchEmailAttachments(accessToken, messageId);
    if (attachments.length === 0) {
      return [];
    }
    
    let allExtractedEvents = [];
    
    // Process each attachment
    for (const attachment of attachments) {
      // Get attachment data
      const attachmentData = await getAttachmentData(accessToken, messageId, attachment.id);
      if (!attachmentData) {
        console.log(`Skipping attachment ${attachment.filename} - could not fetch data`);
        continue;
      }
      
      // Process attachment data
      const processedAttachment = await processAttachment(attachment, attachmentData);
      if (!processedAttachment) {
        console.log(`Skipping attachment ${attachment.filename} - could not process data`);
        continue;
      }
      
      // Skip text-based attachments that are too small (less than 10 characters)
      if (processedAttachment.type === 'text' && 
          processedAttachment.content.length < 10) {
        console.log(`Skipping small text attachment ${attachment.filename}`);
        continue;
      }
      
      // Extract text from attachment with GPT Vision for non-text attachments
      let textContent;
      
      if (processedAttachment.type === 'text') {
        textContent = processedAttachment.content;
      } else {
        textContent = await processAttachmentWithGPTVision(processedAttachment, API_KEY);
      }
      
      if (!textContent) {
        console.log(`No text extracted from attachment ${attachment.filename}`);
        continue;
      }
      
      // Extract events from the text content
      const extractedEvents = await extractEventsFromAttachmentText(
        textContent, emailSubject, API_KEY, emailFrom
      );
      
      if (extractedEvents.length > 0) {
        console.log(`Extracted ${extractedEvents.length} events from attachment ${attachment.filename}`);
        
        // Add attachment info to each event
        const eventsWithAttachmentInfo = extractedEvents.map(event => ({
          ...event,
          attachmentFilename: attachment.filename,
          attachmentType: attachment.mimeType
        }));
        
        allExtractedEvents = [...allExtractedEvents, ...eventsWithAttachmentInfo];
      }
    }
    
    return allExtractedEvents;
  } catch (error) {
    console.error('Error processing email attachments:', error);
    return [];
  }
};